/**
 * This selector pipeline (score → filter → sort) is intentionally split into modular steps
 * to make it easier for the development team to debug, test, and log each transformation stage independently.
 * You could inline everything in one selector, but this structure should improve clarity and maintainability.
 */

import { createSelector } from "@reduxjs/toolkit";
import {
  calculatePracticeScore,
  getMatchLabel,
} from "../../utils/practiceScoringUtils.js";
import { isPracticeVisible } from "../../utils/practiceFilterUtils.js";
import { sortPractices } from "../../utils/practiceSortUtils.js";

/**
 * Step 1: Score and combine metadata for all practices
 */
export const getScoredPractices = (
  allPractices = [],
  rankingScore = [],
  prioritySelections = []
) => {
  return allPractices.map((practice) => {
    const score = calculatePracticeScore(practice, rankingScore);
    const { scoreLabel, scoreClassName } = getMatchLabel(
      score,
      prioritySelections.length
    );

    return {
      ...practice,
      score,
      scoreLabel,
      scoreClassName,
    };
  });
};

/**
 * Step 2: Filter scored practices based on land cover and priorities
 */
export const getFilteredPractices = (
  scoredPractices = [],
  landCoverSelections = [],
  prioritySelections = []
) => {
  return scoredPractices.filter((practice) =>
    isPracticeVisible(practice, landCoverSelections, prioritySelections)
  );
};

/**
 * Step 3: Full pipeline – score, then filter, then sort
 */
export const getFilteredAndSortedPractices = (
  allPractices = [],
  landCoverSelections = [],
  prioritySelections = [],
  rankingScore = []
) => {
  const scored = getScoredPractices(
    allPractices,
    rankingScore,
    prioritySelections
  );
  const filtered = getFilteredPractices(
    scored,
    landCoverSelections,
    prioritySelections
  );

  return sortPractices(filtered);
};

/**
 * Final selector exposed to components
 */
export const selectFilterScoredAndSortedPractices = createSelector(
  (state) => state.practice.allPractices,
  (state) => state.filter.landCoverSelections,
  (state) => state.filter.prioritySelections,
  (state) => state.filter.rankingScore,
  getFilteredAndSortedPractices
);
